import { mathService } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { setCurrencyPrecision } from '../helpers';
import { useChangeNetSuiteRequest, useCurrencyPrecision } from '../hooks';
import { parseDiscountRate } from './NetSuiteInvoiceRequestForm.helpers';
import { netSuiteInvoiceFieldValuesState, netSuiteInvoiceItemListState } from './NetSuiteInvoiceRequestForm.states';
import { NetSuiteInvoiceAmounts, NetSuiteInvoiceFields } from './NetSuiteInvoiceRequestForm.types';

export const useNetSuiteInvoiceAmounts = () => {
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);
    const invoiceItemList = useAtomValue(netSuiteInvoiceItemListState);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const amounts: NetSuiteInvoiceAmounts = useMemo(() => {
        let netAmount = 0;
        let taxAmount = 0;

        invoiceItemList.forEach((item) => {
            netAmount = mathService.add(netAmount, item.amount ?? 0);
            taxAmount = mathService.add(taxAmount, item.taxAmount ?? 0);
        });

        const { discountRate, isDiscountRateAbsolute } = parseDiscountRate(fieldValues.discountRate);

        const transactionDiscount = isDiscountRateAbsolute
            ? discountRate
            : mathService.multiply(netAmount, mathService.multiply(discountRate ?? 0, 0.01));

        const totalTax =
            netAmount !== 0
                ? mathService.add(taxAmount, mathService.multiply(transactionDiscount, taxAmount) / netAmount)
                : 0;

        const totalAmount = mathService.add(mathService.add(netAmount, transactionDiscount), totalTax);

        return {
            netAmount: setCurrencyPrecision(netAmount, currencyPrecision),
            taxAmount: setCurrencyPrecision(totalTax, currencyPrecision),
            totalAmount: setCurrencyPrecision(totalAmount, currencyPrecision),
            transactionDiscount: setCurrencyPrecision(transactionDiscount, currencyPrecision),
        };
    }, [invoiceItemList, currencyPrecision, fieldValues.discountRate]);

    return amounts;
};

export const useNetSuiteInvoiceFields = () => {
    const setFieldValues = useSetAtom(netSuiteInvoiceFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const updateFieldsBulk = useCallback(
        (data: Partial<NetSuiteInvoiceFields>, shouldDispatchChangeEvent = true) => {
            setFieldValues((current) => {
                if (current !== null) {
                    return {
                        ...current,
                        ...data,
                    };
                }

                return current;
            });

            if (shouldDispatchChangeEvent) {
                changeNetSuiteRequest();
            }
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    const onFieldChange = useCallback(
        <K extends keyof NetSuiteInvoiceFields>(key: K, value: NetSuiteInvoiceFields[K]) => {
            setFieldValues((current) => ({
                ...current,
                [key]: value,
            }));

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    return {
        updateFieldsBulk,
        onFieldChange,
    };
};
